<script setup>
import { ref, computed, watch, onBeforeUnmount, onMounted } from 'vue'

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: undefined,
    },
    value: {
        type: Boolean,
        default: undefined,
    },
    title: {
        type: String,
        default: '',
    },
    size: {
        type: String,
        default: '',
    },
    modalClass: {
        type: [String, Array, Object],
        default: '',
    },
    hideHeader: {
        type: Boolean,
        default: false,
    },
    hideFooter: {
        type: Boolean,
        default: false,
    },
    noClose: {
        type: Boolean,
        default: false,
    },
    static: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['update:modelValue', 'input', 'show', 'shown', 'hide', 'hidden'])

const localVisible = ref(false)

// 'static' is a reserved word — alias for template use
const isStatic = () => props.static

const externalVisible = () => props.modelValue ?? props.value ?? false

const dialogClasses = computed(() => ({
    'modal-sm': props.size === 'sm',
    'modal-lg': props.size === 'lg',
    'modal-xl': props.size === 'xl',
}))

function createHideEvent(trigger) {
    return {
        trigger,
        defaultPrevented: false,
        preventDefault() { this.defaultPrevented = true },
    }
}

function doShow() {
    localVisible.value = true
    emit('show')
    lockBody()
}

function doHide(trigger = 'unknown') {
    const event = createHideEvent(trigger)
    emit('hide', event)

    if (event.defaultPrevented) {
        emit('update:modelValue', true)
        emit('input', true)
        return
    }

    localVisible.value = false
    unlockBody()
    emit('update:modelValue', false)
    emit('input', false)
}

watch(externalVisible, (val) => {
    if (val && !localVisible.value) {
        doShow()
    } else if (!val && localVisible.value) {
        doHide('program')
    }
}, { immediate: true, flush: 'sync' })

function onBackdropClick() {
    if (!props.noClose) doHide('backdrop')
}

function lockBody() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    document.body.style.paddingRight = scrollbarWidth + 'px'
    document.body.classList.add('modal-open')
}

function unlockBody() {
    document.body.style.paddingRight = ''
    document.body.classList.remove('modal-open')
}

function onDocumentKeydown(e) {
    if (e.key === 'Escape' && localVisible.value && !props.noClose) {
        doHide('esc')
    }
}

const modalRoot = ref(null)

onMounted(() => {
    if (!props.static) {
        document.body.appendChild(modalRoot.value)
    }
    document.addEventListener('keydown', onDocumentKeydown)
})

onBeforeUnmount(() => {
    document.removeEventListener('keydown', onDocumentKeydown)
    unlockBody()
    if (!props.static && modalRoot.value?.parentNode) {
        modalRoot.value.parentNode.removeChild(modalRoot.value)
    }
})
</script>

<template>
    <div ref="modalRoot">
        <transition name="app-modal" @after-enter="emit('shown')" @after-leave="emit('hidden')">
            <div
                v-if="isStatic() || localVisible"
                class="modal"
                :class="[{ 'd-block': localVisible }, modalClass]"
                role="dialog"
                tabindex="-1"
                @click.self="onBackdropClick"
            >
                <div class="modal-dialog" :class="dialogClasses">
                    <div class="modal-content">

                        <div v-if="!hideHeader" class="modal-header">
                            <slot name="modal-header">
                                <h5 class="modal-title">
                                    <slot name="modal-title">{{ title }}</slot>
                                </h5>
                                <button v-if="!noClose" type="button" class="close" @click="doHide('headerclose')" aria-label="Close"><span aria-hidden="true">&times;</span></button>
                            </slot>
                        </div>

                        <div class="modal-body">
                            <slot :close="doHide"></slot>
                        </div>

                        <div v-if="!hideFooter" class="modal-footer">
                            <slot name="modal-footer">
                                <button type="button" class="btn btn-secondary" @click="doHide('footer')">Close</button>
                            </slot>
                        </div>

                    </div>
                </div>
            </div>
        </transition>

        <transition name="app-backdrop">
            <div v-if="localVisible" class="modal-backdrop" @click="onBackdropClick"></div>
        </transition>
    </div>
</template>

<style scoped>
.modal {
    overflow-x: hidden;
    overflow-y: auto;
    background: transparent;
    z-index: 1050;
}

.modal-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1040;
}

.app-modal-enter-active,
.app-modal-leave-active {
    transition: opacity 0.15s ease;
    display: block !important; /* prevent Bootstrap's display:none from cutting the animation */
}
.app-modal-enter-active .modal-dialog,
.app-modal-leave-active .modal-dialog {
    transition: transform 0.15s ease;
}
.app-modal-enter,
.app-modal-leave-to {
    opacity: 0;
}
.app-modal-enter .modal-dialog,
.app-modal-leave-to .modal-dialog {
    transform: translateY(-20px);
}

.app-backdrop-enter-active,
.app-backdrop-leave-active {
    transition: opacity 0.15s ease;
}
.app-backdrop-enter,
.app-backdrop-leave-to {
    opacity: 0;
}
</style>
